import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MoreVertical, 
  RefreshCw, 
  Square, 
  Trash2, 
  ExternalLink,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Server,
  Cpu,
  HardDrive,
  Clock
} from 'lucide-react';
import axios from 'axios';

export interface Instance {
  uuid: string;
  instance: string;
  status: string;
  cpu: number;
  ram: number;
  disk: number;
  external_ip: string;
  description?: string;
  date_updated?: string;
}

interface InstanceTableProps {
  instances: Instance[];
  onRestart: (instance: Instance) => void;
  onStop: (instance: Instance) => void;
  onTerminate: (instance: Instance) => void;
}

type SortKey = 'instance' | 'description' | 'status' | 'date_updated' | 'cpu' | 'ram' | 'disk' | 'external_ip';
type SortDirection = 'asc' | 'desc';

interface ColumnWidth {
  instance: number;
  description: number;
  status: number;
  date_updated: number;
  cpu: number;
  ram: number;
  disk: number;
  external_ip: number;
  actions: number;
}

const defaultColumnWidths: ColumnWidth = {
  instance: 140,
  description: 180,
  status: 100,
  date_updated: 160,
  cpu: 70,
  ram: 80,
  disk: 90,
  external_ip: 200,
  actions: 100,
};

const InstanceTable: React.FC<InstanceTableProps> = ({
  instances,
  onRestart,
  onStop,
  onTerminate,
}) => {
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [healthStatus, setHealthStatus] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  
  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>('instance');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Column resizing state
  const [columnWidths, setColumnWidths] = useState<ColumnWidth>(defaultColumnWidths);
  const [resizing, setResizing] = useState<{ column: keyof ColumnWidth; startX: number; startWidth: number } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const handleMenuToggle = useCallback((instanceId: string, buttonElement: HTMLButtonElement) => {
    if (menuOpen === instanceId) {
      setMenuOpen(null);
      setMenuPosition(null);
    } else {
      const rect = buttonElement.getBoundingClientRect();
      const menuWidth = 160;
      const menuHeight = 150;
      
      let left = rect.right - menuWidth;
      let top = rect.bottom + 4;
      
      if (left < 8) {
        left = rect.left;
      }
      
      if (top + menuHeight > window.innerHeight - 8) {
        top = rect.top - menuHeight - 4;
      }
      
      setMenuPosition({ top, left });
      setMenuOpen(instanceId);
    }
  }, [menuOpen]);

  const checkHealth = async (ip: string, instanceId: string) => {
    if (!ip) return;
    try {
      const urlObj = new URL(ip.startsWith('http') ? ip : `https://${ip}`);
      const domain = urlObj.hostname.split('.')[0];
      const serverUrl = `https://${domain}server.ecosystem.ai/api/v1/health`;
      const response = await axios.get(serverUrl, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        timeout: 5000,
        validateStatus: (status) => status >= 200 && status < 500,
      });

      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('text/html')) {
        setHealthStatus((prev) => ({ ...prev, [instanceId]: false }));
        return;
      }

      const responseText =
        typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      const isHealthy = responseText.toLowerCase().includes('success');
      setHealthStatus((prev) => ({ ...prev, [instanceId]: isHealthy }));
    } catch {
      setHealthStatus((prev) => ({ ...prev, [instanceId]: false }));
    }
  };

  useEffect(() => {
    instances.forEach((instance) => {
      if (instance.status === 'running' && instance.external_ip) {
        checkHealth(instance.external_ip, instance.instance);
      }
    });
  }, [instances]);

  // Sorting logic
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const sortedInstances = [...instances].sort((a, b) => {
    let aVal: any = a[sortKey];
    let bVal: any = b[sortKey];
    
    // Handle null/undefined
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    
    // Handle dates
    if (sortKey === 'date_updated') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    
    // Handle numbers
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // Handle strings
    const comparison = String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Column resizing handlers
  const handleResizeStart = (column: keyof ColumnWidth, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({
      column,
      startX: e.clientX,
      startWidth: columnWidths[column],
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizing) return;
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(50, resizing.startWidth + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizing.column]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing]);

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'running':
        return 'running';
      case 'stopped':
        return 'stopped';
      case 'terminated':
      case 'failed':
        return 'terminated';
      default:
        return 'pending';
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch {
      return '-';
    }
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortKey !== key) {
      return <ChevronUp size={14} className="sort-icon inactive" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp size={14} className="sort-icon active" />
      : <ChevronDown size={14} className="sort-icon active" />;
  };

  const totalPages = Math.ceil(sortedInstances.length / rowsPerPage);
  const paginatedInstances = sortedInstances.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  return (
    <div style={{ position: 'relative' }}>
      <div className="data-table-container resizable-table">
        <table className="data-table" ref={tableRef}>
          <thead>
            <tr>
              <th style={{ width: columnWidths.instance, minWidth: columnWidths.instance }}>
                <div className="th-content sortable" onClick={() => handleSort('instance')}>
                  <div className="th-label">
                    <Server size={14} />
                    Instance
                  </div>
                  {renderSortIcon('instance')}
                </div>
                <div className="resize-handle" onMouseDown={(e) => handleResizeStart('instance', e)} />
              </th>
              <th style={{ width: columnWidths.description, minWidth: columnWidths.description }}>
                <div className="th-content sortable" onClick={() => handleSort('description')}>
                  <div className="th-label">Description</div>
                  {renderSortIcon('description')}
                </div>
                <div className="resize-handle" onMouseDown={(e) => handleResizeStart('description', e)} />
              </th>
              <th style={{ width: columnWidths.status, minWidth: columnWidths.status }}>
                <div className="th-content sortable" onClick={() => handleSort('status')}>
                  <div className="th-label">Status</div>
                  {renderSortIcon('status')}
                </div>
                <div className="resize-handle" onMouseDown={(e) => handleResizeStart('status', e)} />
              </th>
              <th style={{ width: columnWidths.date_updated, minWidth: columnWidths.date_updated }}>
                <div className="th-content sortable" onClick={() => handleSort('date_updated')}>
                  <div className="th-label">
                    <Clock size={14} />
                    Last Updated
                  </div>
                  {renderSortIcon('date_updated')}
                </div>
                <div className="resize-handle" onMouseDown={(e) => handleResizeStart('date_updated', e)} />
              </th>
              <th style={{ width: columnWidths.cpu, minWidth: columnWidths.cpu }}>
                <div className="th-content sortable" onClick={() => handleSort('cpu')}>
                  <div className="th-label">
                    <Cpu size={14} />
                    CPU
                  </div>
                  {renderSortIcon('cpu')}
                </div>
                <div className="resize-handle" onMouseDown={(e) => handleResizeStart('cpu', e)} />
              </th>
              <th style={{ width: columnWidths.ram, minWidth: columnWidths.ram }}>
                <div className="th-content sortable" onClick={() => handleSort('ram')}>
                  <div className="th-label">RAM (GB)</div>
                  {renderSortIcon('ram')}
                </div>
                <div className="resize-handle" onMouseDown={(e) => handleResizeStart('ram', e)} />
              </th>
              <th style={{ width: columnWidths.disk, minWidth: columnWidths.disk }}>
                <div className="th-content sortable" onClick={() => handleSort('disk')}>
                  <div className="th-label">
                    <HardDrive size={14} />
                    Disk (GB)
                  </div>
                  {renderSortIcon('disk')}
                </div>
                <div className="resize-handle" onMouseDown={(e) => handleResizeStart('disk', e)} />
              </th>
              <th style={{ width: columnWidths.external_ip, minWidth: columnWidths.external_ip }}>
                <div className="th-content sortable" onClick={() => handleSort('external_ip')}>
                  <div className="th-label">External IP</div>
                  {renderSortIcon('external_ip')}
                </div>
                <div className="resize-handle" onMouseDown={(e) => handleResizeStart('external_ip', e)} />
              </th>
              <th className="td-actions" style={{ width: columnWidths.actions, minWidth: columnWidths.actions }}>
                <div className="th-content">
                  <div className="th-label">Actions</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedInstances.map((instance) => {
              const isHealthy = healthStatus[instance.instance];
              return (
                <tr key={instance.uuid || instance.instance}>
                  <td>
                    <span style={{ fontWeight: 500 }}>{instance.instance}</span>
                  </td>
                  <td>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {instance.description || '-'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(instance.status)}`}>
                      {instance.status}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                      {formatDate(instance.date_updated)}
                    </span>
                  </td>
                  <td>{instance.cpu}</td>
                  <td>{instance.ram}</td>
                  <td>{instance.disk}</td>
                  <td>
                    {instance.external_ip ? (
                      instance.status === 'running' ? (
                        <a
                          href={
                            instance.external_ip.startsWith('http')
                              ? instance.external_ip
                              : `https://${instance.external_ip}`
                          }
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: isHealthy ? 'var(--primary)' : 'var(--warning)',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px',
                          }}
                        >
                          {instance.external_ip}
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>{instance.external_ip}</span>
                      )
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>-</span>
                    )}
                  </td>
                  <td className="td-actions">
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        title="View Details"
                        onClick={() =>
                          instance.external_ip &&
                          instance.status === 'running' &&
                          window.open(
                            instance.external_ip.startsWith('http')
                              ? instance.external_ip
                              : `https://${instance.external_ip}`,
                            '_blank'
                          )
                        }
                        disabled={!instance.external_ip || instance.status !== 'running'}
                        style={{
                          opacity:
                            !instance.external_ip || instance.status !== 'running' ? 0.5 : 1,
                        }}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        ref={(el) => { menuButtonRefs.current[instance.instance] = el; }}
                        className="action-btn"
                        title="More Actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuToggle(instance.instance, e.currentTarget);
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dropdown Menu - Rendered outside table to avoid overflow clipping */}
      {menuOpen && menuPosition && (
        <div
          className="actions-dropdown"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--border-radius)',
            boxShadow: 'var(--shadow-lg)',
            minWidth: '160px',
            zIndex: 1000,
          }}
        >
          {(() => {
            const instance = instances.find((i) => i.instance === menuOpen);
            if (!instance) return null;
            return (
              <>
                <button
                  onClick={() => {
                    onRestart(instance);
                    setMenuOpen(null);
                    setMenuPosition(null);
                  }}
                  disabled={
                    instance.status !== 'stopped' &&
                    instance.status !== 'running' &&
                    instance.status !== 'failed'
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    color:
                      instance.status !== 'stopped' &&
                      instance.status !== 'running' &&
                      instance.status !== 'failed'
                        ? 'var(--text-disabled)'
                        : 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    cursor:
                      instance.status !== 'stopped' &&
                      instance.status !== 'running' &&
                      instance.status !== 'failed'
                        ? 'not-allowed'
                        : 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    if (
                      instance.status === 'stopped' ||
                      instance.status === 'running' ||
                      instance.status === 'failed'
                    ) {
                      e.currentTarget.style.background = 'var(--bg-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <RefreshCw size={14} />
                  Restart
                </button>
                <button
                  onClick={() => {
                    onStop(instance);
                    setMenuOpen(null);
                    setMenuPosition(null);
                  }}
                  disabled={instance.status !== 'running'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    color:
                      instance.status !== 'running'
                        ? 'var(--text-disabled)'
                        : 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    cursor: instance.status !== 'running' ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    if (instance.status === 'running') {
                      e.currentTarget.style.background = 'var(--bg-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Square size={14} />
                  Stop
                </button>
                <div
                  style={{
                    height: '1px',
                    background: 'var(--border-color)',
                    margin: '4px 0',
                  }}
                />
                <button
                  onClick={() => {
                    onTerminate(instance);
                    setMenuOpen(null);
                    setMenuPosition(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--danger)',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--danger-faded)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Trash2 size={14} />
                  Terminate
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          <div className="pagination-info">
            Page {page + 1} of {totalPages}
          </div>
          <button
            className="pagination-btn"
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Click outside to close menu */}
      {menuOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
          }}
          onClick={() => {
            setMenuOpen(null);
            setMenuPosition(null);
          }}
        />
      )}
    </div>
  );
};

export default InstanceTable;
